﻿import { Button } from 'devextreme-react/button';
import { FileMetadata } from '../../models/Common/file-metadata';
import { useMemo } from 'react';

interface Props {
    file: FileMetadata;
    canRemove: boolean;
    onRemove?: () => void;
    height?: number | string;
}

export default function FilePreview(props: Props) {
    const { url, name, contentType } = props.file;

    // Normalize height prop to CSS string
    const frameHeight = props.height
        ? (typeof props.height === 'number' ? `${props.height}px` : props.height)
        : '100%';

    /**
     * Helpers to classify the file for preview rendering.
     * We use both contentType and file extension as fallbacks.
     */
    const ext = useMemo(() => {
        const dot = name.lastIndexOf('.');
        return dot >= 0 ? name.substring(dot + 1).toLowerCase() : '';
    }, [name]);

    const isPdf = useMemo(() => {
        const byCT = contentType?.toLowerCase() === 'application/pdf';
        const byExt = name?.toLowerCase().endsWith('.pdf');
        return byCT || byExt;
    }, [contentType, name]);

    const isImage = useMemo(() => {
        if (contentType?.startsWith('image/')) return true;
        return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
    }, [contentType, ext]);

    // If needed later could add video/audio detection:
    // const isVideo = contentType?.startsWith('video/') || ['mp4', 'webm'].includes(ext);

    return (
        <div className="file-preview-container">
            {!!props.onRemove && (
                <Button
                    className="file-preview-remove-btn"
                    icon="close"
                    onClick={() => { if (props.canRemove) props.onRemove?.(); }}
                    disabled={!props.canRemove}
                    aria-disabled={!props.canRemove}
                    hint={!props.canRemove ? 'This document is read-only' : undefined}
                />
            )}

            {isPdf ? (
                <iframe
                    className="file-preview-pdf-iframe"
                    src={`${url}#toolbar=0&navpanes=0`}
                    title={name || 'PDF Preview'}
                    width="100%"
                    height={frameHeight}
                />
            ) : isImage ? (
                <img
                    className="file-preview-image"
                    src={url}
                    alt={name || 'Image preview'}
                />
            ) : (
                <div
                    className="file-preview-placeholder"
                    style={{ height: frameHeight }}
                >
                    <div>
                        <i className="dx-icon-doc de-default-icon-color dx-default-icon-size-md" />
                        <div>
                            Preview not available for this file type.
                        </div>
                        <div className="file-preview-type">
                            {contentType || (ext ? `.${ext}` : 'unknown type')}
                        </div>
                    </div>
                </div>
            )}

            {/*<div style={{ marginTop: 1, marginLeft:2, fontSize:15, overflowWrap: 'anywhere' }}>*/}
            {/*    {name || 'Document'}*/}
            {/*</div>*/}
        </div>
    );
}